/**
 * E60 — the ONE world↔screen transform for the SiteCanvas, DOM-free so the
 * inverse is unit-testable at every angle (the bugs live in un-inverted
 * pointer paths, which fail only when rotated).
 *
 * View rotation is a CAMERA: display-only, never computed with, never
 * persisted. It is a different thing from the per-surface module-grid
 * rotation (`gridRotationDeg`), which changes the layout and IS persisted.
 *
 * Frames: world = canvas-local metres (x east, y south — the E56 display
 * frame); screen = pixels. worldToScreen scales by S about (cx,cy), then
 * rotates by rotationDeg about the canvas centre. Rotation is an isometry of
 * the screen: pixel distances (hit radii) are preserved at every angle.
 */

export interface View {
  S: number; // px per metre
  cx: number;
  cy: number;
  /** E60 — camera rotation, degrees clockwise on screen. Display-only. */
  rotationDeg?: number;
}

export function worldToScreen(
  q: { x: number; y: number },
  view: View,
  cw: number,
  ch: number,
): { x: number; y: number } {
  const dx = (q.x - view.cx) * view.S;
  const dy = (q.y - view.cy) * view.S;
  const r = ((view.rotationDeg ?? 0) * Math.PI) / 180;
  if (r === 0) return { x: dx + cw / 2, y: dy + ch / 2 };
  return {
    x: dx * Math.cos(r) - dy * Math.sin(r) + cw / 2,
    y: dx * Math.sin(r) + dy * Math.cos(r) + ch / 2,
  };
}

/** Exact inverse of {@link worldToScreen} — EVERY pointer path goes through this. */
export function screenToWorld(
  p: { x: number; y: number },
  view: View,
  cw: number,
  ch: number,
): { x: number; y: number } {
  const px = p.x - cw / 2;
  const py = p.y - ch / 2;
  const r = ((view.rotationDeg ?? 0) * Math.PI) / 180;
  const dx = r === 0 ? px : px * Math.cos(r) + py * Math.sin(r);
  const dy = r === 0 ? py : -px * Math.sin(r) + py * Math.cos(r);
  return { x: view.cx + dx / view.S, y: view.cy + dy / view.S };
}

/** Pan by a SCREEN delta (drag): the world shift depends on the rotation. */
export function panView(view: View, screenDx: number, screenDy: number): View {
  const r = ((view.rotationDeg ?? 0) * Math.PI) / 180;
  const dxw = (screenDx * Math.cos(r) + screenDy * Math.sin(r)) / view.S;
  const dyw = (-screenDx * Math.sin(r) + screenDy * Math.cos(r)) / view.S;
  return { ...view, cx: view.cx - dxw, cy: view.cy - dyw };
}

/** Screen direction of world NORTH (world −y): drives the north arrow ONLY —
 *  the scale bar stays unrotated because rotation preserves pixel lengths. */
export function northAngleRad(view: View): number {
  return ((view.rotationDeg ?? 0) * Math.PI) / 180;
}

export function rotateView(view: View, byDeg: number): View {
  const next = (((view.rotationDeg ?? 0) + byDeg) % 360 + 360) % 360;
  return { ...view, rotationDeg: next };
}

/** One click back to north — a planner must never lose orientation. */
export function resetNorth(view: View): View {
  return { ...view, rotationDeg: 0 };
}
